import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Check, X, Pencil, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-slate-200 text-slate-700" };
const KIND = { field: "Contact / record field", staff_departure: "Staff departure", staff_update: "Staff details", staff_addition: "New staff / producer" };
const DETAIL = { first_name: "First name", last_name: "Last name", email: "Email", mobile: "Mobile", relationship_type: "Relationship", relationship: "Relationship", position: "Position", employment: "Employment", remote: "Remote", pc_licensed: "P&C licensed", lh_licensed: "L&H licensed", note: "Note" };

const VAL = { producer: "Contracted Producer", employee: "Employee", full_time: "Full-Time", part_time: "Part-Time", staff_at: "Agent Staff link", producer_at: "Producer link", ended: "Agency link ended" };

const show = (v) => {
  if (v === null || v === undefined || v === "") return <span className="text-slate-400 italic">empty</span>;
  if (Array.isArray(v)) return v.map((x) => VAL[x] || x).join(", ");
  if (typeof v === "object") return Object.entries(v).filter(([k, x]) => k !== "added" && x !== null && typeof x !== "object").map(([k, x]) => `${DETAIL[k] || k}: ${x === true ? "Yes" : VAL[x] || x}`).join(" · ");
  if (VAL[v]) return VAL[v];
  return String(v);
};

function Compare({ c }) {
  const live = c.kind === "field" ? c.current_value : c.current_at_submit;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center mt-2" data-testid={`corr-compare-${c.id}`}>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2"><p className="text-[11px] uppercase tracking-wide text-slate-400">{c.status === "approved" ? "Previous" : "Current"}</p>
        <p className="text-sm text-slate-800 break-words">{show(c.status === "approved" ? c.previous_value : live)}</p></div>
      <ArrowRight className="w-4 h-4 text-slate-400 hidden sm:block" />
      <div className="rounded-md border border-sky-200 bg-sky-50 p-2"><p className="text-[11px] uppercase tracking-wide text-sky-600">{c.status === "approved" ? "Applied" : "Proposed"}</p>
        <p className="text-sm text-slate-900 font-medium break-words">{show(c.status === "approved" && c.kind === "field" ? c.applied_value : c.proposed_value)}</p></div>
    </div>
  );
}

function Matches({ c, choice, setChoice }) {
  if (!c.matches) return null;
  return (
    <div className="mt-2 rounded-md border border-slate-200 p-2 text-sm" data-testid={`corr-matches-${c.id}`}>
      <p className="text-xs font-semibold text-slate-600 mb-1">{c.matches.length ? "Possible existing people — link instead of creating a duplicate:" : "No existing person matches this name, email or phone."}</p>
      {c.matches.map((m, i) => (
        <label key={i} className="flex items-center gap-2 py-0.5"><input type="radio" disabled={!m.same_group} checked={!!m.person_id && choice === m.person_id} onChange={() => setChoice(m.person_id)} data-testid={`corr-match-${c.id}-${i}`} />
          {m.name}{m.human_id ? ` (${m.human_id})` : ""}{m.email ? ` · ${m.email}` : ""}<span className="text-[11px] text-slate-400">{m.confidence === "confident" ? "same email/phone" : "same name"}</span></label>
      ))}
      {!c.matches.some((m) => m.confidence === "confident") && (
        <label className="flex items-center gap-2 py-0.5"><input type="radio" checked={choice === "new"} onChange={() => setChoice("new")} data-testid={`corr-match-${c.id}-new`} />Create a new person</label>)}
    </div>
  );
}

function Item({ c, canDecide, reload }) {
  const [edit, setEdit] = useState(null);
  const [reason, setReason] = useState(null);
  const [choice, setChoice] = useState(c.matches && !c.matches.length ? "new" : null);
  const [busy, setBusy] = useState(false);
  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(`/form-corrections/${c.id}/${path}`, body); toast.success(path === "approve" ? "Correction applied" : "Correction rejected"); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const approve = () => {
    if (c.kind === "staff_addition") {
      if (!choice) return toast.error("Choose an existing person or Create a new person");
      return act("approve", choice === "new" ? { create_new: true } : { link_person_id: choice });
    }
    return act("approve", edit !== null ? { value: edit } : {});
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`corr-item-${c.id}`}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{c.field_label}<span className="ml-2 text-xs font-normal text-slate-400">{KIND[c.kind]}</span></p>
          <p className="text-xs text-slate-500">{c.agent_name}{c.agency_name ? ` · ${c.agency_name}` : ""} · Record: {c.target_name || "—"} · {c.form_name} {c.review_year}</p>
          <p className="text-xs text-slate-400">Submitted {new Date(c.submitted_at).toLocaleDateString()} by {c.submitted_by_name || "agent"}</p>
        </div>
        <span className={`ml-auto text-xs font-semibold rounded-full px-2 py-0.5 capitalize ${STATUS[c.status]}`} data-testid={`corr-status-${c.id}`}>{c.status}</span>
      </div>
      <Compare c={c} />
      {c.note && <p className="text-xs text-slate-500 mt-1">Agent note: {c.note}</p>}
      {c.status === "pending" && canDecide && <Matches c={c} choice={choice} setChoice={setChoice} />}
      {c.status !== "pending" && <p className="text-xs text-slate-500 mt-2" data-testid={`corr-decision-${c.id}`}>{c.status === "approved" ? "Approved" : "Rejected"} by {c.decided_by_name} on {new Date(c.decided_at).toLocaleString()}{c.edited_before_apply ? " · edited before applying" : ""}{c.reason ? ` · Reason: ${c.reason}` : ""}</p>}
      {c.status === "pending" && canDecide && (
        <div className="mt-3 space-y-2">
          {edit !== null && <Input value={edit} onChange={(e) => setEdit(e.target.value)} data-testid={`corr-edit-input-${c.id}`} />}
          {reason !== null && <Input value={reason} placeholder="Optional reason" onChange={(e) => setReason(e.target.value)} data-testid={`corr-reason-${c.id}`} />}
          <div className="flex flex-wrap gap-2">
            {reason === null && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={approve} data-testid={`corr-approve-${c.id}`}><Check className="w-4 h-4 mr-1" />{edit !== null ? "Apply edited value" : "Approve"}</Button>}
            {c.kind === "field" && edit === null && reason === null && <Button size="sm" variant="outline" onClick={() => setEdit(String(c.proposed_value ?? ""))} data-testid={`corr-edit-${c.id}`}><Pencil className="w-4 h-4 mr-1" />Edit Before Applying</Button>}
            {edit === null && (reason === null
              ? <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setReason("")} data-testid={`corr-reject-${c.id}`}><X className="w-4 h-4 mr-1" />Reject</Button>
              : <><Button size="sm" variant="destructive" disabled={busy} onClick={() => act("reject", { reason })} data-testid={`corr-reject-confirm-${c.id}`}>Confirm Reject</Button>
                <Button size="sm" variant="ghost" onClick={() => setReason(null)}>Cancel</Button></>)}
            {edit !== null && <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FormCorrections() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/form-corrections", { params: { form_id: id, status: status || undefined } }).then((r) => setD(r.data)).catch((e) => { toast.error(apiError(e)); setD({ items: [], counts: {} }); });
  }, [id, status]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-slate-400 p-6">Loading…</p>;
  return (
    <div className="max-w-4xl" data-testid="form-corrections-page">
      <button onClick={() => navigate(`/admin/forms/${id}/assignments`)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-brand mb-3"><ChevronLeft className="w-4 h-4" />Survey Assignments</button>
      <h1 className="font-display text-2xl font-bold text-slate-900">Proposed Corrections</h1>
      <p className="text-sm text-slate-500 mb-4">Agents' submitted corrections never change records until approved here. Approving updates only that field or agency link on the existing record; rejecting leaves the live value unchanged.</p>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="corr-filters">
        {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`corr-filter-${k || "all"}`}
            className={`text-xs font-semibold rounded-full px-3 py-1 border ${status === k ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"}`}>{l}{k ? ` (${d.counts?.[k] || 0})` : ""}</button>
        ))}
      </div>
      {!d.can_decide && <p className="text-xs text-slate-500 mb-3">View only — only a Group Admin or Super Admin can approve or reject corrections.</p>}
      <div className="space-y-3">
        {d.items.map((c) => <Item key={c.id} c={c} canDecide={d.can_decide} reload={load} />)}
        {!d.items.length && <p className="text-sm text-slate-400 py-8 text-center" data-testid="corr-empty">Nothing here.</p>}
      </div>
    </div>
  );
}
